import { useCallback, useEffect, useMemo, useState } from "react";

import { FirebaseAuth } from "../components/FirebaseAuth/firebase";
import { buildMonthlySeries, formatMoney, monthKeyFromDate, toDate } from "../libs/dashboardOverview";
import { normalizeContractDoc } from "../libs/firestoreSchema";

const toFriendlyError = (error, fallback) => {
  const raw = (error && error.message ? String(error.message) : "").trim();
  if (!raw) return fallback;
  return raw.replace(/^Firebase:\s*/i, "").replace(/\s*\(auth\/[^)]+\)\.?$/i, "").trim();
};

const mapRateChangeRow = (row = {}) => ({
  ...row,
  requestId: row.request_id || row.id || "",
  contractId: row.contract_id || "",
  accountId: row.account_id || "",
  clientUid: row.client_uid || "",
  contractorUid: row.contractor_uid || "",
  productId: row.product_id || "",
  pricingModel: row.pricing_model || "",
  currentPriceId: row.current_price_id || "",
  requestedPriceId: row.requested_price_id || "",
  currentHourlyCents: Number(row.current_hourly_cents || 0),
  fromHourlyCents: Number(row.current_hourly_cents || 0),
  requestedHourlyCents: Number(row.requested_hourly_cents || 0),
  currentMonthlyCents: Number(row.current_monthly_cents || 0),
  fromMonthlyCents: Number(row.current_monthly_cents || 0),
  requestedMonthlyCents: Number(row.requested_monthly_cents || 0),
  effectiveDate: row.effective_date || "",
  effectiveFrom: row.effective_from || null,
  requestedByAdminUid: row.requested_by_admin_uid || "",
  approvedByClientUid: row.approved_by_client_uid || "",
  rejectedByClientUid: row.rejected_by_client_uid || "",
  createdAt: row.created_at || null,
  updatedAt: row.updated_at || null,
});

const useClientDashboard = () => {
  const uid = FirebaseAuth.auth().currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [profile, setProfile] = useState({ firstName: "", lastName: "", companyName: "", country: "", accountId: "", customerId: "" });
  const [contracts, setContracts] = useState([]);
  const [rateChangeRequests, setRateChangeRequests] = useState([]);
  const [scheduledRateChanges, setScheduledRateChanges] = useState([]);
  const [rateChangeAuditRequests, setRateChangeAuditRequests] = useState([]);
  const [busyRateRequestIds, setBusyRateRequestIds] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceRowsPerPage, setInvoiceRowsPerPage] = useState(5);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(5);

  const loadDashboard = useCallback(async () => {
    if (!uid) return;

    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const userSnap = await FirebaseAuth.firestore().collection("users").doc(uid).get();
      const userData = userSnap.exists ? userSnap.data() || {} : {};
      const accountId = String(userData.active_account_id || "");
      const customerId = accountId;
      const firstName = userData.fname || userData.first_name || "";
      const lastName = userData.lname || userData.last_name || "";
      const companyName = String(
        userData.company_name ||
        userData.company?.name ||
        userData.company ||
        "Client"
      );
      const country = String(userData.company_address?.country || userData.country || "").toUpperCase();
      setProfile({ firstName, lastName, accountId, customerId, companyName, country });

      const billingBackfillCompleted = Boolean(userData.billing_backfill_completed);
      if (!billingBackfillCompleted && customerId) {
        try {
          const backfill = FirebaseAuth.functions().httpsCallable("backfillCustomerBillingData");
          await backfill({ force: false });
        } catch (_backfillError) {
          // Keep dashboard loading from mirrored Firestore data even if backfill is temporarily unavailable.
        }
      }

      const contractsPromise = uid
        ? Promise.allSettled([
            FirebaseAuth.firestore().collection("contracts").where("client_uid", "==", uid).get(),
          ])
        : Promise.resolve(null);
      const rateChangesPromise = customerId
        ? FirebaseAuth.functions().httpsCallable("listClientContractRateChanges")({})
        : Promise.resolve({ data: { requests: [], scheduled_requests: [], audit_requests: [] } });
      const invoicesPromise = uid
        ? FirebaseAuth.firestore().collection("users").doc(uid).collection("invoices").get()
        : Promise.resolve(null);
      const paymentsPromise = uid
        ? FirebaseAuth.firestore().collection("users").doc(uid).collection("payments").get()
        : Promise.resolve(null);

      const [contractsResult, rateChangesResult, invoicesResult, paymentsResult] = await Promise.allSettled([
        contractsPromise,
        rateChangesPromise,
        invoicesPromise,
        paymentsPromise,
      ]);

      if (contractsResult.status === "fulfilled" && contractsResult.value) {
        const byId = new Map();
        contractsResult.value.forEach((result) => {
          if (result.status !== "fulfilled") return;
          result.value.docs.forEach((d) => {
            byId.set(d.id, normalizeContractDoc(d.id, d.data() || {}));
          });
        });
        setContracts(Array.from(byId.values()));
      } else {
        setContracts([]);
      }

      if (rateChangesResult.status === "fulfilled") {
        setRateChangeRequests(Array.isArray(rateChangesResult.value?.data?.requests) ? rateChangesResult.value.data.requests.map(mapRateChangeRow) : []);
        setScheduledRateChanges(Array.isArray(rateChangesResult.value?.data?.scheduled_requests) ? rateChangesResult.value.data.scheduled_requests.map(mapRateChangeRow) : []);
        setRateChangeAuditRequests(Array.isArray(rateChangesResult.value?.data?.audit_requests) ? rateChangesResult.value.data.audit_requests.map(mapRateChangeRow) : []);
      } else {
        setRateChangeRequests([]);
        setScheduledRateChanges([]);
        setRateChangeAuditRequests([]);
      }

      if (invoicesResult.status === "fulfilled" && invoicesResult.value) {
        const rows = invoicesResult.value.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort((a, b) => Number(b.created || 0) - Number(a.created || 0));
        setInvoices(rows);
      } else {
        setInvoices([]);
      }

      if (paymentsResult.status === "fulfilled" && paymentsResult.value) {
        const rows = paymentsResult.value.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort((a, b) => Number(b.created || 0) - Number(a.created || 0));
        setPayments(rows);
      } else {
        setPayments([]);
      }

      const firstError = [contractsResult, rateChangesResult, invoicesResult, paymentsResult].find((r) => r.status === "rejected");
      if (firstError && !String(firstError.reason?.message || "").toLowerCase().includes("missing or insufficient permissions")) {
        setMessage({ type: "warning", text: toFriendlyError(firstError.reason, "Some dashboard data could not be loaded.") });
      }
    } catch (error) {
      setMessage({ type: "error", text: toFriendlyError(error, "Failed to load client dashboard data.") });
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!uid) return undefined;
    return FirebaseAuth.firestore()
      .collection("contracts")
      .where("client_uid", "==", uid)
      .onSnapshot((snapshot) => {
        setContracts(snapshot.docs.map((doc) => normalizeContractDoc(doc.id, doc.data() || {})));
      }, () => null);
  }, [uid]);

  const spending = useMemo(() => {
    const nowEpoch = Math.floor(Date.now() / 1000);
    const amountDue = invoices.reduce((sum, i) => {
      const status = String(i.status || "").toLowerCase();
      const remaining = Number(i.amount_remaining || 0);
      if (["open", "past_due", "uncollectible"].includes(status)) return sum + remaining;
      return sum;
    }, 0);
    const overdue = invoices.reduce((sum, i) => {
      const status = String(i.status || "").toLowerCase();
      const remaining = Number(i.amount_remaining || 0);
      const dueDate = Number(i.due_date || 0);
      if (remaining > 0 && dueDate > 0 && dueDate < nowEpoch && !["paid", "void", "draft"].includes(status)) {
        return sum + remaining;
      }
      return sum;
    }, 0);
    const totalPaid = invoices.reduce((sum, i) => sum + Number(i.amount_paid || 0), 0);
    return {
      amountDue,
      totalPaid,
      overdue,
    };
  }, [invoices]);

  const overview = useMemo(() => {
    const activeContracts = contracts.filter((row) => String(row.status || "").toLowerCase() === "active");
    const openRequests = contracts.filter((row) => ["submitted", "shortlisted", "matched", "signed"].includes(String(row.status || "").toLowerCase()));
    const pendingInvoices = invoices.filter((row) => {
      const status = String(row.status || "").toLowerCase();
      const remaining = Number(row.amount_remaining || row.amount_due || 0);
      return remaining > 0 && !["paid", "void"].includes(status);
    });
    const successfulPayments = payments.filter((row) => ["succeeded", "paid"].includes(String(row.status || "").toLowerCase()));
    const buildPaymentSeries = (months) => buildMonthlySeries(
      successfulPayments,
      (row) => monthKeyFromDate(toDate(row.created)),
      (row) => Number(row.amount || row.amount_received || 0),
      months
    );
    const overdueInvoices = invoices.filter((row) => {
      const status = String(row.status || "").toLowerCase();
      const remaining = Number(row.amount_remaining || row.amount_due || 0);
      const dueDate = Number(row.due_date || 0);
      return remaining > 0 && dueDate > 0 && dueDate < Math.floor(Date.now() / 1000) && !["paid", "void", "draft"].includes(status);
    });
    const contractStatusCounts = ["submitted", "shortlisted", "matched", "signed", "active", "closed"].map((status) => ({
      label: status.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value: contracts.filter((row) => String(row.status || "").toLowerCase() === status).length,
    }));
    const serviceSpend = Object.values(contracts.reduce((acc, row) => {
      const label = String(row.roleLabel || row.roleTitle || "Uncategorized").trim();
      const amount = Number(row.monthlyBudgetCents || row.hourlyBudgetCents || row.suggestedMonthlyCents || row.suggestedHourlyCents || 0);
      acc[label] = acc[label] || { label, value: 0 };
      acc[label].value += amount > 0 ? amount : 0;
      return acc;
    }, {})).sort((a, b) => Number(b.value || 0) - Number(a.value || 0)).slice(0, 6);

    return {
      summary: [
        { label: "Active contracts", value: activeContracts.length, helper: "Current engagements" },
        { label: "Open requests", value: openRequests.length, helper: "Hiring pipeline" },
        { label: "Amount due", value: formatMoney(spending.amountDue), helper: `${pendingInvoices.length} pending invoice${pendingInvoices.length === 1 ? "" : "s"}` },
        { label: "Total paid", value: formatMoney(spending.totalPaid), helper: "Across mirrored invoices" },
      ],
      seriesByRange: {
        3: buildPaymentSeries(3),
        6: buildPaymentSeries(6),
        12: buildPaymentSeries(12),
      },
      breakdowns: {
        engagements: contractStatusCounts,
        billing: [
          { label: "Paid", value: spending.totalPaid },
          { label: "Due", value: spending.amountDue },
          { label: "Overdue", value: overdueInvoices.reduce((sum, row) => sum + Number(row.amount_remaining || row.amount_due || 0), 0) },
        ],
        serviceSpend,
      },
      metrics: [],
      series: buildPaymentSeries(6),
    };
  }, [contracts, invoices, payments, spending.amountDue, spending.totalPaid]);

  const navBadges = useMemo(() => ({
    engagements: contracts.filter((row) => String(row.status || "").toLowerCase() !== "active").length + rateChangeRequests.length,
    billing: invoices.filter((row) => {
      const status = String(row.status || "").toLowerCase();
      const remaining = Number(row.amount_remaining || row.amount_due || 0);
      return remaining > 0 && !["paid", "void"].includes(status);
    }).length,
  }), [contracts, invoices, rateChangeRequests.length]);

  const submitPosition = async (hireForm) => {
    if (!uid || !profile.accountId) {
      setMessage({ type: "error", text: "Client account is not linked correctly. Please contact support." });
      return;
    }

    setMessage({ type: "", text: "" });
    try {
      const createHireRequest = FirebaseAuth.functions().httpsCallable("clientCreateHireRequest");
      await createHireRequest({
        account_id: profile.accountId,
        role_code: hireForm.roleCode || "",
        role_label: hireForm.roleLabel || "",
        role_title: hireForm.roleTitle.trim(),
        role_level: hireForm.roleLevel || "",
        role_level_label: hireForm.roleLevelLabel || "",
        role_family: hireForm.roleFamily || "",
        role_specialty: hireForm.roleSpecialty || hireForm.roleCode || "",
        role_seniority: hireForm.roleSeniority || hireForm.roleLevel || "",
        requirements: hireForm.requirements.trim(),
        requirements_html: String(hireForm.requirementsHtml || "").trim(),
        requirements_format: hireForm.requirementsFormat || "plain",
        project_term_months: hireForm.projectTermMonths || null,
        desired_start_date: hireForm.desiredStartDate || "",
        pricing_model: hireForm.pricingModel,
        currency: hireForm.currency || "usd",
        suggested_hourly: Number(hireForm.suggestedHourly || 0),
        suggested_monthly: Number(hireForm.suggestedMonthly || 0),
        hourly_budget: Number(hireForm.hourlyBudget || 0),
        monthly_budget: Number(hireForm.monthlyBudget || 0),
      });
      setMessage({ type: "success", text: "Hire request submitted. You can now find contractors." });
      await loadDashboard();
    } catch (error) {
      setMessage({ type: "error", text: toFriendlyError(error, "Failed to submit position request.") });
      throw error;
    }
  };

  const updateContract = async (contractId, updates) => {
    if (!uid || !contractId) return;
    setMessage({ type: "", text: "" });
    try {
      const updateHireRequest = FirebaseAuth.functions().httpsCallable("clientUpdateHireRequest");
      const response = await updateHireRequest({
        contract_id: contractId,
        expected_version: Number(updates.expectedVersion || updates.version || 0),
        role_code: updates.roleCode || "",
        role_label: updates.roleLabel || "",
        role_title: String(updates.roleTitle || "").trim(),
        role_level: updates.roleLevel || "",
        role_level_label: updates.roleLevelLabel || "",
        role_family: updates.roleFamily || "",
        role_specialty: updates.roleSpecialty || updates.roleCode || "",
        role_seniority: updates.roleSeniority || updates.roleLevel || "",
        requirements: String(updates.requirements || "").trim(),
        requirements_html: String(updates.requirementsHtml || "").trim(),
        requirements_format: updates.requirementsFormat || "plain",
        project_term_months: updates.projectTermMonths || null,
        desired_start_date: updates.desiredStartDate || "",
        pricing_model: updates.pricingModel,
        currency: updates.currency || "usd",
        suggested_hourly: Number(updates.suggestedHourly || 0),
        suggested_monthly: Number(updates.suggestedMonthly || 0),
        hourly_budget: Number(updates.hourlyBudget || 0),
        monthly_budget: Number(updates.monthlyBudget || 0),
      });
      await loadDashboard();
      const invalidated = Number(response?.data?.invalidated_candidate_count || 0);
      setMessage({ type: "success", text: invalidated > 0 ? `Request updated. ${invalidated} previous candidate match${invalidated === 1 ? " was" : "es were"} cleared.` : "Request updated." });
    } catch (error) {
      setMessage({ type: "error", text: toFriendlyError(error, "Failed to update request.") });
      throw error;
    }
  };

  const withdrawContract = async (contractId, expectedVersion = 0) => {
    const fn = FirebaseAuth.functions().httpsCallable("clientWithdrawHireRequest");
    try {
      await fn({ contract_id: String(contractId), expected_version: Number(expectedVersion || 0) });
      await loadDashboard();
      setMessage({ type: "success", text: "Hire request withdrawn." });
      return { ok: true };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to withdraw hire request.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const generateContractMatches = async (contractId, expectedVersion = 0) => {
    if (!uid || !contractId) return { ok: false, message: "Contract is not available." };
    setMessage({ type: "", text: "" });
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientGenerateCandidateMatches");
      const result = await fn({
        contract_id: String(contractId),
        expected_version: Number(expectedVersion || 0),
      });
      await loadDashboard();
      const count = Number(result?.data?.added_candidate_count || 0);
      const text = result?.data?.message || (count > 0
        ? `${count} new contractor match${count === 1 ? "" : "es"} added.`
        : "No additional contractor matches were found.");
      setMessage({ type: "success", text });
      return { ok: true, message: text, data: result?.data || {} };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to generate contractor matches.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const selectContractor = async (contractId, candidateUid, expectedVersion = 0) => {
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientSelectContractor");
      await fn({ contract_id: String(contractId), candidate_uid: String(candidateUid), expected_version: Number(expectedVersion || 0) });
      await loadDashboard();
      setMessage({ type: "success", text: "Contractor selected and sent to Ops51 for contract approval." });
      return { ok: true };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to select contractor.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const recordInterviewOutcome = async (contractId, candidateUid, outcome, expectedVersion = 0) => {
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientRecordInterviewOutcome");
      await fn({ contract_id: String(contractId), candidate_uid: String(candidateUid), outcome, expected_version: Number(expectedVersion || 0) });
      await loadDashboard();
      setMessage({ type: "success", text: outcome === "completed" ? "Candidate marked as interviewed." : "Interview marked as no-show." });
      return { ok: true };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to update interview outcome.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const requestCandidateInterview = async (contractId, candidateUid, messageText = "", expectedVersion = 0) => {
    if (!uid || !contractId || !candidateUid) return { ok: false, message: "Candidate is not available." };
    setMessage({ type: "", text: "" });
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientRequestCandidateInterview");
      const result = await fn({
        contract_id: String(contractId),
        candidate_uid: String(candidateUid),
        expected_version: Number(expectedVersion || 0),
        message: String(messageText || "").trim(),
      });
      await loadDashboard();
      const text = "Interview request sent.";
      setMessage({ type: "success", text });
      return { ok: true, message: text, data: result?.data || {} };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to request interview.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const selectCandidateInterviewSlot = async (contractId, candidateUid, slotIndex, expectedVersion = 0) => {
    if (!uid || !contractId || !candidateUid) return { ok: false, message: "Interview slot is not available." };
    setMessage({ type: "", text: "" });
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientSelectCandidateInterviewSlot");
      const result = await fn({
        contract_id: String(contractId),
        candidate_uid: String(candidateUid),
        slot_index: Number(slotIndex),
        expected_version: Number(expectedVersion || 0),
      });
      await loadDashboard();
      const text = "Interview time selected.";
      setMessage({ type: "success", text });
      return { ok: true, message: text, data: result?.data || {} };
    } catch (error) {
      const text = toFriendlyError(error, "Failed to select interview time.");
      setMessage({ type: "error", text });
      return { ok: false, message: text };
    }
  };

  const resolveContractRateChange = async (requestId, decision, reason = "") => {
    if (!requestId || !["approved", "rejected"].includes(String(decision || "").toLowerCase())) return;
    const row = (Array.isArray(rateChangeRequests) ? rateChangeRequests : []).find((r) => String(r?.id || "") === String(requestId));
    const effectiveDate = String(row?.effectiveDate || "").trim();
    setBusyRateRequestIds((prev) => ({ ...prev, [requestId]: true }));
    try {
      const fn = FirebaseAuth.functions().httpsCallable("clientResolveContractRateChange");
      await fn({
        request_id: String(requestId),
        decision: String(decision).toLowerCase(),
        reason: String(reason || ""),
      });
      const successText = decision === "approved"
        ? `Rate change approved. Pricing will update on the effective date${effectiveDate ? ` (${effectiveDate})` : ""}.`
        : "Rate change rejected.";
      await loadDashboard();
      return { ok: true, message: successText };
    } catch (error) {
      return { ok: false, message: toFriendlyError(error, "Failed to resolve rate change request.") };
    } finally {
      setBusyRateRequestIds((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  return {
    billingPagination: {
      invoicePage,
      invoiceRowsPerPage,
      onInvoicePageChange: (_e, p) => setInvoicePage(p),
      onInvoiceRowsPerPageChange: (e) => {
        setInvoiceRowsPerPage(parseInt(e.target.value, 10));
        setInvoicePage(0);
      },
      onPaymentPageChange: (_e, p) => setPaymentPage(p),
      onPaymentRowsPerPageChange: (e) => {
        setPaymentRowsPerPage(parseInt(e.target.value, 10));
        setPaymentPage(0);
      },
      paymentPage,
      paymentRowsPerPage,
    },
    busyRateRequestIds,
    contracts,
    invoices,
    loading,
    message,
    navBadges,
    overview,
    payments,
    profile,
    rateChangeAuditRequests,
    rateChangeRequests,
    generateContractMatches,
    requestCandidateInterview,
    recordInterviewOutcome,
    selectContractor,
    selectCandidateInterviewSlot,
    resolveContractRateChange,
    scheduledRateChanges,
    spending,
    submitPosition,
    updateContract,
    withdrawContract,
  };
};

export default useClientDashboard;
